import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
} from '@mui/material';
import {
  AccountTree,
  Forum,
  Timeline,
  PeopleAlt,
  ArrowForward,
  ArrowDownward,
  School,
  Science,
  Memory,
  FiberManualRecord,
} from '@mui/icons-material';
import { GlassCard, Kicker, Logo, AppButton, StatPill } from '../components/ui';
import { mvkgTokens, glassSx } from '../theme/mvkgTokens';

/* ── Static content (translated from new_ui/MVKG/MVKG Landing.html) ──────── */

const PROBLEM_CARDS = [
  { big: '11', unit: 'views', label: 'Lenses on the same graph', hint: 'Citation alone misses competition, synergy and disruption.' },
  { big: 'CD5', unit: '', label: 'Disruption score · per method', hint: 'Breakthrough vs consolidating — automatically, on load.' },
  { big: 'Multi', unit: 'agent', label: 'LangGraph extraction', hint: 'Classifier, meta, tech, eval and alias agents in concert.' },
  { big: 'MCP', unit: '', label: 'Agent-native interface', hint: 'Query 11 views over REST or MCP JSON-RPC.' },
];

// Restrained, semantic accents. The landing should read as a research product,
// not an assortment of generated gradient tiles.
const FEATURE_ACCENTS = {
  graph:     { color: '#4338ca', surface: 'rgba(79,70,229,0.10)' },
  agent:     { color: '#6d28d9', surface: 'rgba(124,58,237,0.10)' },
  discovery: { color: '#b45309', surface: 'rgba(245,158,11,0.13)' },
  coreaders: { color: '#047857', surface: 'rgba(16,185,129,0.12)' },
};

const FEATURES = [
  {
    icon: AccountTree,
    accent: 'graph',
    label: '01 · Map the corpus',
    title: 'See the structure behind a paper set.',
    description:
      'A paper is not a citation. It is a Method that solves Problems, uses Components and achieves Metrics on Datasets — and disrupts what came before. We extract all of it.',
    bullets: [
      ['Genealogy', 'parent-child evolution of methods.'],
      ['Competition', 'which methods solve the same problem.'],
      ['Synergy (PMI)', 'which methods are commonly combined.'],
      ['Disruption', 'breakthrough vs consolidating, per-method CD5.'],
      ['Forecast', 'predicted links — what is about to matter.'],
    ],
  },
  {
    icon: Forum,
    accent: 'agent',
    label: '02 · Investigate with evidence',
    title: 'Ask questions with sources, not guesses.',
    description:
      'Ask any question. The agent calls real tools — graph.search, graph.competition, rag.papers — and grounds every claim in your actual corpus. You see which tools it called and which sources it read.',
    bullets: [
      ['Multi-step reasoning', 'the agent walks several tools per query.'],
      ['Persistent memory', 'sessions are stored, debuggable and shareable.'],
      ['MCP-native', 'plug into Cursor, Claude Desktop or any agent.'],
      ['Provider-agnostic', 'Claude, Gemini, GPT or local Ollama.'],
    ],
  },
  {
    icon: Timeline,
    accent: 'discovery',
    label: '03 · See momentum',
    title: 'Track what is changing in the field.',
    description:
      'Temporal deltas surface what is growing, stable and declining. Research-direction clustering groups emerging work so you see momentum before the survey papers catch up.',
    bullets: [
      ['Temporal summary', 'growing / stable / declining at a glance.'],
      ['Emerging directions', 'automatic clustering of new work.'],
      ['CD5 disruption', 'scored on every load, not once a year.'],
    ],
  },
  {
    icon: PeopleAlt,
    accent: 'coreaders',
    label: '04 · Find the people',
    title: 'Find collaborators through reading overlap.',
    description:
      'Author roles reveal who pioneers and who consolidates. Reading overlap connects people working the same frontier — across labs and across the corpus.',
    bullets: [
      ['Author roles', 'ideators, consolidators and connectors.'],
      ['Reading overlap', 'people working the same problem space.'],
      ['Lineage', 'how a method traces back through its authors.'],
    ],
  },
];

const PIPELINE = [
  { step: '01 · Ingest', title: 'PDF / DOI / ORCID', body: 'Drop a PDF, paste a DOI, or sync ORCID. ZIPs are recursively extracted.' },
  { step: '02 · Map', title: 'Classify & chunk', body: 'An LLM identifies bibliography pages and document type. Long PDFs roll through in chunks.' },
  { step: '03 · Extract', title: 'LangGraph multi-agent', body: 'Classifier, meta, tech, eval and alias agents. Methods, Problems, Components, Claims.' },
  { step: '04 · Score', title: 'CD5 & PMI', body: 'Per-method disruption, synergy PMI and domain convergence — recomputed on every load.' },
  { step: '05 · Serve', title: 'NebulaGraph + RAG', body: '11 views queryable via REST or MCP JSON-RPC, with real-time CDC sync.' },
];

const STACK = ['FastAPI', 'LangGraph', 'NebulaGraph', 'Celery + Redis', 'React 18 · MUI', 'MCP · JSON-RPC', 'Kafka · Debezium CDC'];

const AUDIENCE = [
  { icon: School, title: 'PhD candidates', body: 'Map an unfamiliar subfield in a weekend. Survey ten years of literature with one collection and find the blind spots.' },
  { icon: Science, title: 'Research labs', body: 'A shared graph across the lab. Track which methods are emerging in your subfield. Find collaborators through reading overlap.' },
  { icon: Memory, title: 'R&D teams', body: 'Plug MVKG into your agents via MCP. Track competitor methods and auto-generate briefings with grounded citations.' },
];

/* ── Live corpus stats (public, no-auth endpoint) ───────────────────────── */
// The landing is pre-login, so it calls the dedicated public endpoint
// (`/api/public/landing-stats`, Redis-cached server-side) with a plain fetch
// and NO auth header. On any failure we keep `stats` null and every consumer
// falls back to its illustrative static string — the page never looks broken.
function useLandingStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let active = true;
    fetch('/api/public/landing-stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (active && data) setStats(data);
      })
      .catch(() => {
        /* keep static fallbacks */
      });
    return () => {
      active = false;
    };
  }, []);
  return stats;
}

// Compact integer formatter: 6957 → "6,957", 1234567 → "1.2M".
function fmtNum(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  return n.toLocaleString('en-US');
}

/* ── Hero knowledge-graph visualization (lightweight inline SVG) ─────────── */
// Recoloured 2026-05-28: previous version was ~10 indigo dots + one red blob,
// which read as "single-colour blob" and gave no hint that the actual product
// renders 14 entity types in their own colour/shape. Now each peripheral node
// uses the real palette from `Graph/tokens.js` (Method indigo, Problem amber,
// Component cyan, Dataset green, Author lime, Claim purple, Technique slate)
// so the hero teaser previews the legend the user will see inside the app.
function HeroGraph({ topMethod }) {
  // Real most-disruptive method when the public stats loaded; otherwise the
  // illustrative example below stays (clearly a sample, never presented as a
  // live-but-fake figure). Long names are truncated so the SVG label fits.
  const hasReal = topMethod && topMethod.name && typeof topMethod.cd5 === 'number';
  const heroName = hasReal
    ? (topMethod.name.length > 26 ? `${topMethod.name.slice(0, 25)}…` : topMethod.name)
    : 'Multi-Head Attention';
  const heroCd5 = hasReal
    ? `CD5 ${topMethod.cd5 >= 0 ? '+' : ''}${topMethod.cd5.toFixed(2)}`
    : 'CD5 +0.41 · example';
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 460,
        mx: 'auto',
        aspectRatio: '1 / 1',
        ...glassSx(true),
        borderRadius: `${mvkgTokens.radius.xl}px`,
        p: 2,
      }}
    >
      <Box component="svg" viewBox="0 0 480 480" sx={{ width: '100%', height: '100%' }}>
        <defs>
          <radialGradient id="lp-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lp-edge" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.05" />
            <stop offset="50%" stopColor="#4f46e5" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="lp-forecast" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F4511E" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#F4511E" stopOpacity="0.5" />
          </linearGradient>
        </defs>
        <circle cx="240" cy="240" r="230" fill="url(#lp-halo)" />
        {/* Background lattice — neutral, no longer the headline visual */}
        <g stroke="#94a3b8" strokeWidth="1" strokeOpacity="0.35" fill="none">
          <line x1="240" y1="240" x2="140" y2="150" />
          <line x1="240" y1="240" x2="350" y2="140" />
          <line x1="240" y1="240" x2="380" y2="250" />
          <line x1="240" y1="240" x2="330" y2="370" />
          <line x1="240" y1="240" x2="140" y2="360" />
          <line x1="240" y1="240" x2="90" y2="250" />
          <line x1="140" y1="150" x2="90" y2="250" />
          <line x1="350" y1="140" x2="410" y2="90" />
          <line x1="380" y1="250" x2="430" y2="340" />
        </g>
        {/* Predicted (FORECAST) edge: a sober dashed line, not decorative motion. */}
        <line x1="240" y1="240" x2="350" y2="140" stroke="url(#lp-forecast)" strokeWidth="2" strokeDasharray="4 4" />
        {/* Hero method node: breakthrough + CD5 score. */}
        <circle cx="240" cy="240" r="30" fill="#ef4444" fillOpacity="0.12" />
        <circle cx="240" cy="240" r="20" fill="#ef4444" />
        <text x="240" y="290" textAnchor="middle" fontSize="12" fontWeight="700" fill="#0f172a" fontFamily="Inter">{heroName}</text>
        <text x="240" y="305" textAnchor="middle" fontSize="10" fontWeight="600" fill="#ef4444" fontFamily="JetBrains Mono">{heroCd5}</text>
        {/* Peripheral entities — each typed and coloured per real palette:
              indigo  = Method        (Graph/tokens.NODE_COLORS.method)
              amber   = Problem
              cyan    = Component
              green   = Dataset       (square, matches WebGL Dataset shape)
              lime    = Author
              purple  = Claim
              slate   = Technique     (triangle, matches WebGL shape)         */}
        <circle cx="140" cy="150" r="13" fill="#4f46e5" />            {/* Method  */}
        <circle cx="350" cy="140" r="12" fill="#4f46e5" />            {/* Method  */}
        <circle cx="380" cy="250" r="11" fill="#f59e0b" />            {/* Problem */}
        <circle cx="330" cy="370" r="9"  fill="#06b6d4" />            {/* Component */}
        <circle cx="140" cy="360" r="11" fill="#84cc16" />            {/* Author  */}
        <circle cx="90"  cy="250" r="9"  fill="#a855f7" />            {/* Claim   */}
        <rect   x="402"  y="82"  width="14" height="14" rx="2" fill="#22c55e" />              {/* Dataset square */}
        <polygon points="425,330 435,345 415,345"            fill="#64748b" />                {/* Technique triangle */}
      </Box>
    </Box>
  );
}

function FeatureBlock({ feature }) {
  const Icon = feature.icon;
  // Per-feature accent is semantic but quiet: the content, rather than an
  // oversized gradient icon, carries the hierarchy.
  const accent = FEATURE_ACCENTS[feature.accent] || FEATURE_ACCENTS.graph;
  // Single-column block: the old two-column layout left one half empty save
  // for a small icon tile, which read as a broken responsive grid. Inlining
  // the icon next to the kicker keeps the visual hierarchy without the
  // "half the card is empty" effect.
  return (
    <GlassCard
      padding={{ xs: 3, md: 4 }}
      sx={{
        mb: { xs: 3, md: 4 },
      }}
    >
      <Stack direction="row" spacing={{ xs: 2, md: 3 }} alignItems="flex-start">
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: 48, md: 56 },
            height: { xs: 48, md: 56 },
            borderRadius: `${mvkgTokens.radius.md}px`,
            bgcolor: accent.surface,
            border: `1px solid ${accent.color}2e`,
            color: accent.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'none',
          }}
        >
          <Icon sx={{ fontSize: { xs: 24, md: 28 } }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Kicker sx={{ color: accent.color }}>{feature.label}</Kicker>
          <Typography
            variant="h5"
            sx={{
              fontFamily: mvkgTokens.font.display,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              mt: 0.5,
              mb: 1.5,
            }}
          >
            {feature.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2 }}>
            {feature.description}
          </Typography>
          <Stack spacing={1}>
            {feature.bullets.map(([head, tail]) => (
              <Stack key={head} direction="row" spacing={1.25} alignItems="flex-start">
                <FiberManualRecord sx={{ fontSize: 9, color: 'primary.main', mt: '7px' }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{head}</Box>
                  {' — '}{tail}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </GlassCard>
  );
}

export default function Landing() {
  const stats = useLandingStats();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Real corpus figures (null until the public endpoint resolves). `views`
  // defaults to the genuine constant 11; the rest only render when present.
  const viewsCount = (stats && typeof stats.views === 'number' && stats.views) || 11;
  const liveStats = [
    { value: fmtNum(stats?.total_entities), label: 'entities in the graph' },
    { value: fmtNum(stats?.total_methods), label: 'methods extracted' },
    { value: fmtNum(stats?.articles_processed), label: 'papers processed' },
    { value: fmtNum(stats?.total_datasets), label: 'datasets linked' },
  ].filter((s) => s.value != null);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        // Neutral page bg — the previous full-page `gradient.page` (three
        // accumulating indigo/purple radial washes) read as a moody fog over
        // the long scroll. Keep the page itself clean off-white and reserve
        // the wash for the hero only.
        bgcolor: '#f7f8fa',
        color: 'text.primary',
      }}
    >
      {/* ── Nav ──────────────────────────────────────────────────────── */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          ...glassSx(true),
          borderRadius: 0,
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: 64 }}>
            <Box component="button" onClick={scrollToTop} sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0 }}>
              <Logo size="md" tagline />
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <AppButton variant="ghost" size="sm" onClick={() => scrollTo('what')} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                Workflow
              </AppButton>
              <AppButton component={RouterLink} to="/login" size="sm">
                Sign in
              </AppButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <Box
        component="section"
        sx={{
          // Localised wash — a single soft indigo halo behind the hero,
          // not three radial gradients smeared over the entire page.
          position: 'relative',
          backgroundImage:
            'radial-gradient(ellipse 70% 60% at 20% 30%, rgba(99,102,241,0.07), transparent 65%)',
        }}
      >
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 12 } }}>
        <Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
              <FiberManualRecord sx={{ fontSize: 10, color: 'success.main' }} />
              <Kicker sx={{ color: 'text.secondary' }}>Now in private beta · for research labs</Kicker>
            </Stack>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontFamily: mvkgTokens.font.display,
                fontWeight: 700,
                letterSpacing: '-0.03em',
                lineHeight: 1.08,
                mb: 2.5,
                fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
              }}
            >
              Map a research field before you read{' '}
              <Box component="span" sx={{ background: mvkgTokens.gradient.brand, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              everything
              </Box>
              .
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, lineHeight: 1.65, maxWidth: 540, mb: 4, fontSize: { xs: '1.05rem', md: '1.18rem' } }}>
              Upload a paper collection. MVKG groups methods into research directions, shows the evidence between them, and gives you a clear path into the part of the field that matters.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <AppButton component={RouterLink} to="/login" size="lg" endIcon={<ArrowForward />}>
                Open the workspace
              </AppButton>
              <AppButton variant="secondary" size="lg" endIcon={<ArrowDownward />} onClick={() => scrollTo('what')}>
                See the workflow
              </AppButton>
            </Stack>
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ mt: 4 }}>
              <StatPill label="Views" value={`${viewsCount} lenses`} accent="brand" />
              <StatPill label="Score" value="CD5 disruption" accent="rose" />
              <StatPill label="Interface" value="MCP-native" accent="purple" />
            </Stack>
            {/* Live corpus figures — only render once the public stats endpoint
                resolves; on failure the strip is simply absent (no fake zeros). */}
            {liveStats.length > 0 && (
              <Stack
                direction="row"
                spacing={{ xs: 3, sm: 4 }}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 4 }}
              >
                {liveStats.map((s) => (
                  <Box key={s.label}>
                    <Typography
                      sx={{
                        fontFamily: mvkgTokens.font.display,
                        fontWeight: 700,
                        fontSize: '1.6rem',
                        letterSpacing: '-0.02em',
                        lineHeight: 1,
                      }}
                    >
                      {s.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {s.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <HeroGraph topMethod={stats?.top_method} />
          </Grid>
        </Grid>
      </Container>
      </Box>

      {/* ── Problem / value cards ────────────────────────────────────── */}
      <Container maxWidth="lg" component="section" sx={{ py: { xs: 5, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Kicker sx={{ color: 'primary.main' }}>The problem</Kicker>
          <Typography variant="h3" sx={{ fontFamily: mvkgTokens.font.display, fontWeight: 700, letterSpacing: '-0.02em', mt: 1, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
            Turn a paper collection into a research map.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 640, mx: 'auto', lineHeight: 1.65 }}>
            Citation graphs and keyword search flatten a field into a long list. MVKG makes the methods, problems and evidence visible together, so you can decide where to start.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {PROBLEM_CARDS.map((c) => {
            // The "views" card mirrors the real lens count from the public
            // stats endpoint (defaults to the genuine constant 11).
            const big = c.unit === 'views' ? viewsCount : c.big;
            return (
            <Grid item xs={12} sm={6} md={3} key={c.label}>
              <GlassCard padding={3} sx={{ height: '100%' }}>
                <Typography sx={{ fontFamily: mvkgTokens.font.display, fontWeight: 700, fontSize: '2.2rem', letterSpacing: '-0.02em', lineHeight: 1 }}>
                  {big}
                  {c.unit && (
                    <Box component="span" sx={{ fontSize: '0.9rem', color: 'text.secondary', ml: 0.5, fontWeight: 600 }}>
                      {c.unit}
                    </Box>
                  )}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5 }}>{c.label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>{c.hint}</Typography>
              </GlassCard>
            </Grid>
            );
          })}
        </Grid>
      </Container>

      {/* ── Features ─────────────────────────────────────────────────── */}
      <Box id="what" component="section" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
            <Kicker sx={{ color: 'primary.main' }}>What it does</Kicker>
            <Typography variant="h3" sx={{ fontFamily: mvkgTokens.font.display, fontWeight: 700, letterSpacing: '-0.02em', mt: 1, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              Four ways to make a literature review more deliberate.
            </Typography>
          </Box>
          {FEATURES.map((f) => (
            <FeatureBlock key={f.title} feature={f} />
          ))}
        </Container>
      </Box>

      {/* ── Under the hood / pipeline ────────────────────────────────── */}
      <Box id="under" component="section" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Kicker sx={{ color: 'primary.main' }}>Under the hood</Kicker>
            <Typography variant="h3" sx={{ fontFamily: mvkgTokens.font.display, fontWeight: 700, letterSpacing: '-0.02em', mt: 1, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              A pipeline, not an embedding.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 660, mx: 'auto', lineHeight: 1.65 }}>
              Most "AI for research" tools dump papers into a vector store and call it a day. We run a multi-agent extraction pipeline that builds a real, structured knowledge graph — auditable, queryable and re-runnable.
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {PIPELINE.map((p) => (
              <Grid item xs={12} sm={6} md key={p.step}>
                <GlassCard padding={3} sx={{ height: '100%' }}>
                  <Kicker sx={{ color: 'primary.main' }}>{p.step}</Kicker>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 0.75 }}>{p.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>{p.body}</Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
            <Kicker sx={{ mr: 1 }}>Stack</Kicker>
            {STACK.map((s) => (
              <StatPill key={s} value={s} accent="gray" />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── For whom ─────────────────────────────────────────────────── */}
      <Box id="who" component="section" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Kicker sx={{ color: 'primary.main' }}>For whom</Kicker>
            <Typography variant="h3" sx={{ fontFamily: mvkgTokens.font.display, fontWeight: 700, letterSpacing: '-0.02em', mt: 1, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              Three kinds of people save days a week.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {AUDIENCE.map(({ icon: Icon, title, body }) => (
              <Grid item xs={12} md={4} key={title}>
                <GlassCard padding={3.5} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: `${mvkgTokens.radius.md}px`,
                      bgcolor: 'rgba(79,70,229,0.1)',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>{body}</Typography>
                </GlassCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <Container maxWidth="md" component="section" sx={{ py: { xs: 6, md: 10 } }}>
        <GlassCard
          strong
          padding={{ xs: 4, md: 6 }}
          sx={{ textAlign: 'center', boxShadow: mvkgTokens.shadow.pop }}
        >
          <Typography variant="h3" sx={{ fontFamily: mvkgTokens.font.display, fontWeight: 700, letterSpacing: '-0.02em', mb: 1.5, fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
            Start with one paper collection.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3.5, lineHeight: 1.65 }}>
            Sign in to upload papers, build a research map and investigate it with grounded questions.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            <AppButton component={RouterLink} to="/login" size="lg" endIcon={<ArrowForward />}>
              Open the workspace
            </AppButton>
            <AppButton variant="ghost" size="lg" onClick={() => scrollTo('what')}>
              See the workflow
            </AppButton>
          </Stack>
        </GlassCard>
      </Container>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <Box component="footer" sx={{ ...glassSx(true), borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderBottom: 'none', py: 5 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Logo size="sm" />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, maxWidth: 420, lineHeight: 1.6 }}>
                A workspace for evidence-led scientific literature review.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <AppButton component={RouterLink} to="/login" variant="ghost" size="sm">
                Sign in
              </AppButton>
              <Typography variant="body2" color="text.secondary">
                {'©'} {new Date().getFullYear()} MVKG
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
